/**
 * "My work" (ADR-0025): what this person should do next. Reporters see
 * drafts, returned surveys and sites due; analysts and account managers see
 * their queues. Works offline from local drafts and the offline pack.
 */
import { useEffect, useMemo, useState } from 'react';
import { optimizeSurveyRoute } from '@sss/form-engine';
import type { SurveyStatus } from '@sss/workflow';
import {
  Badge,
  Banner,
  Button,
  Card,
  Dialog,
  EmptyState,
  Field,
  followUpTone,
  Icon,
  KpiTile,
  SectionTitle,
  Segmented,
  Select,
  Skeleton,
  SEVERITY_TONE,
  SURVEY_STATUS_TONE,
  Textarea,
} from '../../components';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../lib/api/client';
import type { MyWorkFindingRow, MyWorkSurveyRow, PackSite, Severity } from '../../lib/api/types';
import { useI18n } from '../../lib/i18n';
import { Link, useNavigate } from '../../lib/router';
import { daysToFollowUpOf } from '../../lib/sync/pack';
import { useDrafts, useMyWork, usePackSites } from '../../app/hooks';
import { useMe } from '../../app/me';
import { useServices } from '../../app/services';
import { SurveyIdLabel } from '../capture/CapturePages';
import { SiteCard } from '../sites/SiteCard';

/** Rows shown per Home section; "View all" opens the full, filtered list. */
const PREVIEW = 5;

/** Filtered desk lists the Home sections link to. */
export const HOME_LINKS = {
  reviewQueue: '/review',
  findingsToVerify: '/findings?status=RESOLVED,RISK_ACCEPTED',
  readyToSend: '/surveys?tab=all&status=CONFIRMED',
  findingsWithoutPlan: '/findings?status=COMMUNICATED',
} as const;

function ViewAll({ to }: { to: string }) {
  const { t } = useI18n();
  return (
    <Link to={to} className="text-sm font-semibold text-brand">
      {t('ui.home.viewAll')}
    </Link>
  );
}

function FindingRow({ f }: { f: MyWorkFindingRow }) {
  const { t } = useI18n();
  const days = f.days_to_follow_up;
  return (
    <Link to={`/findings/${f.id}`}>
      <Card interactive className="flex items-center gap-3 p-3">
        <Badge tone={SEVERITY_TONE[f.severity] ?? 'neutral'}>{t(`severity.${f.severity}`)}</Badge>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold">{f.site_name}</p>
          <p className="truncate text-sm text-muted">{[f.human_id, f.customer_name].filter(Boolean).join(' · ')}</p>
        </div>
        {days !== undefined && days !== null ? (
          <Badge tone={followUpTone(days)}>
            <span className="tabular-nums">{days < 0 ? t('ui.desk.daysOverdue', { count: -days }) : t('ui.desk.daysLeft', { count: days })}</span>
          </Badge>
        ) : (
          f.status && <Badge tone="success">{t(`findingStatus.${f.status}`)}</Badge>
        )}
      </Card>
    </Link>
  );
}

function SurveyRow({ s, to, status }: { s: MyWorkSurveyRow; to: string; status?: SurveyStatus }) {
  const { t, fmt } = useI18n();
  const st = s.status ?? status;
  return (
    <Link to={to}>
      <Card interactive className="flex items-center gap-3 p-3.5">
        <span className={`flex size-9 shrink-0 items-center justify-center rounded-lg ${st === 'RETURNED' ? 'bg-warning-soft text-warning' : 'bg-brand-soft text-brand'}`}>
          <Icon name={st === 'RETURNED' ? 'flag' : 'clipboard'} size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold">{s.site_name}</p>
          <p className="truncate text-sm text-muted">
            {[s.human_id, s.customer_name].filter(Boolean).join(' · ')}
            {s.review_deadline && ` · ${t('ui.home.reviewBy', { date: fmt.date(s.review_deadline) })}`}
          </p>
        </div>
        {st && <Badge tone={SURVEY_STATUS_TONE[st] ?? 'neutral'}>{t(`surveyStatus.${st}`)}</Badge>}
      </Card>
    </Link>
  );
}

interface MySiteBulletin {
  id: string;
  title: string;
  description: string;
  severity: Severity;
  questions: string[];
  sites: { siteId: string; siteName: string; verified: boolean; status: string | null; verifiedAt: string | null }[];
}

export function HomePage() {
  const { t } = useI18n();
  const { me, can } = useMe();
  const { api, sync } = useServices();
  const toast = useToast();
  const navigate = useNavigate();
  const work = useMyWork();
  const drafts = useDrafts();
  const pack = usePackSites();
  const myDrafts = (drafts.data ?? []).filter((d) => d.reporterUserId === me.user.id);
  const inProgress = myDrafts.filter((d) => d.status === 'DRAFT' || d.status === 'RETURNED');
  const waiting = myDrafts.filter((d) => d.syncState === 'pending' && d.status === 'SUBMITTED');
  const workMissing = work.isError && work.error instanceof ApiError && (work.error.status === 404 || work.error.isNetwork);

  const [bulletins, setBulletins] = useState<MySiteBulletin[]>([]);
  const [activeBulletin, setActiveBulletin] = useState<MySiteBulletin | null>(null);
  const [verifySiteId, setVerifySiteId] = useState('');
  const [verifyStatus, setVerifyStatus] = useState<'SAFE' | 'ACTION_REQUIRED'>('SAFE');
  const [verifyNotes, setVerifyNotes] = useState('');
  const [checkedQuestions, setCheckedQuestions] = useState<Record<number, boolean>>({});
  const [verifying, setVerifying] = useState(false);
  const [preloadingRoute, setPreloadingRoute] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get<MySiteBulletin[]>('/safety/bulletins/my-sites')
      .then((res) => {
        if (!cancelled && Array.isArray(res)) {
          setBulletins(res.filter((b) => b.sites.some((s) => !s.verified)));
        }
      })
      .catch(() => {
        /* offline or not reachable */
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  // Due sites: from the API (it knows every site the reporter is named on), else from
  // due dates kept on the device's sites (offline / API not reachable).
  const dueSites = useMemo<PackSite[]>(() => {
    const fromApi = (work.data?.reporter?.dueSites ?? [])
      .filter((d) => d.daysToFollowUp !== null && d.daysToFollowUp <= 30)
      .map(
        (d): PackSite =>
          pack.data?.find((p) => p.id === d.siteId) ?? {
            id: d.siteId,
            code: d.siteCode,
            name: d.siteName,
            customerId: d.customerId,
            customerName: d.customerName,
            orgPath: '',
            branchCode: d.branchCode,
            serviceLine: d.serviceLine,
            enrolments: [{ surveyType: d.surveyType, nextDueDate: d.nextDueDate, daysToFollowUp: d.daysToFollowUp }],
          },
      );
    if (work.data?.reporter) return fromApi.slice(0, 8);
    // Days worked out for today on the device, so they never freeze offline (ADR-0014).
    const days = (s: PackSite) => (s.enrolments ?? []).map((e) => daysToFollowUpOf(e)).filter((d): d is number => d !== null);
    return (pack.data ?? [])
      .filter((s) => days(s).some((d) => d <= 30))
      .sort((a, b) => Math.min(...days(a)) - Math.min(...days(b)))
      .slice(0, 8);
  }, [pack.data, work.data]);

  const optimizedRoute = useMemo(() => {
    if (dueSites.length < 2) return [];
    return optimizeSurveyRoute(
      dueSites.map((s) => {
        const minDays = Math.min(
          ...(s.enrolments ?? [])
            .map((e) => e.daysToFollowUp ?? daysToFollowUpOf(e))
            .filter((d): d is number => d !== null && d !== undefined),
          30,
        );
        return {
          siteId: s.id,
          siteName: s.name,
          customerName: s.customerName ?? '',
          lat: s.lat ?? null,
          lng: s.lng ?? null,
          daysUntilDue: Number.isFinite(minDays) ? minDays : 30,
          openCriticalFindings: 0,
        };
      }),
    );
  }, [dueSites]);

  const firstName = (me.user.name || me.user.email).split(/[\s@]/)[0];
  const reporter = can('survey.create');
  // Server-side drafts / returned surveys not on this device (other phone, or returned by the analyst).
  const returnedServer = (work.data?.reporter?.drafts ?? []).filter((r) => !myDrafts.some((d) => d.id === r.id));

  const submitBulletinVerification = async () => {
    if (!activeBulletin || !verifySiteId) return;
    setVerifying(true);
    try {
      const answers =
        activeBulletin.questions.length > 0
          ? Object.fromEntries(
              activeBulletin.questions.map((_q, idx) => [`Q${idx + 1}`, checkedQuestions[idx] !== false ? 'YES' : 'NO']),
            )
          : undefined;
      await api.post(`/safety/bulletins/${activeBulletin.id}/verify`, {
        siteId: verifySiteId,
        status: verifyStatus,
        note: verifyNotes.trim() || undefined,
        answers,
      });
      toast.show(t('ui.safety.bulletin.verifiedToast'), 'success');
      setBulletins((prev) =>
        prev
          .map((b) =>
            b.id === activeBulletin.id
              ? {
                  ...b,
                  sites: b.sites.map((s) => (s.siteId === verifySiteId ? { ...s, verified: true, status: verifyStatus } : s)),
                }
              : b,
          )
          .filter((b) => b.sites.some((s) => !s.verified)),
      );
      setActiveBulletin(null);
      setVerifyNotes('');
    } catch {
      toast.show(t('ui.safety.errorToast'), 'danger');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="mb-3 border-b border-border/80 pb-4">
        <h1 className="font-display text-2xl font-bold tracking-tight">{t('ui.home.hello', { name: firstName })}</h1>
        <p className="mt-1 text-sm text-muted">{t('ui.home.subtitle')}</p>
      </div>

      {bulletins.map((b) => {
        const unverified = b.sites.filter((s) => !s.verified);
        const primaryQuestion = b.questions[0] ?? b.title;
        return (
          <Banner
            key={b.id}
            tone={b.severity === 'CRITICAL' ? 'danger' : 'warning'}
            title={`${t('ui.safety.bulletin.homeBannerTitle')}: ${b.title}`}
            action={
              <Button
                size="sm"
                variant="danger"
                icon="shield"
                onClick={() => {
                  setActiveBulletin(b);
                  setVerifySiteId(unverified[0]?.siteId ?? '');
                  setVerifyStatus('SAFE');
                  setVerifyNotes('');
                  setCheckedQuestions(
                    Object.fromEntries(b.questions.map((_q, idx) => [idx, true])),
                  );
                }}
              >
                {t('ui.safety.bulletin.verifyButton')}
              </Button>
            }
          >
            <p className="text-sm">{b.description}</p>
            <p className="mt-1 text-xs font-semibold">
              {t('ui.safety.bulletin.questionLabel')}: {primaryQuestion}
            </p>
          </Banner>
        );
      })}

      {reporter && (
        <Button size="lg" icon="plus" onClick={() => navigate('/sites')} className="self-stretch sm:self-start">
          {t('ui.home.startSurvey')}
        </Button>
      )}

      {reporter && (inProgress.length > 0 || returnedServer.length > 0) && (
        <>
          <SectionTitle>{t('ui.home.continue')}</SectionTitle>
          <div className="flex flex-col gap-2">
            {inProgress.map((d) => {
              const site = pack.data?.find((s) => s.id === d.siteId);
              return (
                <Link key={d.id} to={`/capture/${d.id}`}>
                  <Card interactive className="flex items-center gap-3 p-3.5">
                    <span className={`flex size-9 shrink-0 items-center justify-center rounded-lg ${d.status === 'RETURNED' ? 'bg-warning-soft text-warning' : 'bg-brand-soft text-brand'}`}>
                      <Icon name={d.status === 'RETURNED' ? 'flag' : 'clipboard'} size={18} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold">{site?.name ?? t('ui.capture.survey')}</p>
                      <p className="truncate text-sm">
                        <SurveyIdLabel draft={d} />
                      </p>
                    </div>
                    <Badge tone={SURVEY_STATUS_TONE[d.status] ?? 'neutral'}>{t(`surveyStatus.${d.status}`)}</Badge>
                  </Card>
                </Link>
              );
            })}
            {returnedServer.map((s) => (
              <SurveyRow key={s.id} s={s} to={`/surveys/${s.id}`} />
            ))}
          </div>
        </>
      )}

      {waiting.length > 0 && (
        <>
          <SectionTitle>{t('ui.home.waitingToSync')}</SectionTitle>
          <Card className="flex items-center gap-3 p-4">
            <Icon name="cloud" className="text-info" />
            <span className="flex-1">{t('ui.home.waitingBody', { count: waiting.length })}</span>
            <Link to="/profile#sync" className="font-semibold text-brand">
              {t('ui.home.details')}
            </Link>
          </Card>
        </>
      )}

      {reporter && optimizedRoute.length >= 2 && (
        <>
          <SectionTitle
            action={
              <Button
                size="sm"
                variant="secondary"
                icon="download"
                loading={preloadingRoute}
                onClick={async () => {
                  setPreloadingRoute(true);
                  try {
                    await sync.flush();
                    toast.show(t('ui.excellence.route.preloadedToast', { count: optimizedRoute.length }), 'success');
                  } finally {
                    setPreloadingRoute(false);
                  }
                }}
              >
                {t('ui.excellence.route.preloadAll')}
              </Button>
            }
          >
            {t('ui.excellence.route.title')}
          </SectionTitle>
          <Card className="p-3.5">
            <p className="mb-2.5 text-xs text-muted">{t('ui.excellence.route.subtitle')}</p>
            <ol className="flex flex-col divide-y divide-border/60">
              {optimizedRoute.map((stop, idx) => {
                const daysDue = stop.daysUntilDue ?? 30;
                return (
                  <li key={stop.siteId} className="flex items-center justify-between gap-2 py-2 text-sm">
                    <div className="flex min-w-0 items-center gap-2.5">
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-soft font-mono text-xs font-bold text-brand">
                        {idx + 1}
                      </span>
                      <Link to={`/sites/${stop.siteId}`} className="truncate font-semibold hover:text-brand">
                        {stop.siteName}
                      </Link>
                      {stop.customerName && <span className="truncate text-xs text-muted">{stop.customerName}</span>}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {stop.legDistanceKm !== null && stop.legDistanceKm > 0 && (
                        <span className="text-xs text-muted">
                          {t('ui.excellence.route.driveEst', {
                            km: stop.legDistanceKm,
                            min: stop.estimatedDriveMinutes ?? 0,
                            mins: stop.estimatedDriveMinutes ?? 0,
                          })}
                        </span>
                      )}
                      <Badge tone={daysDue < 0 ? 'danger' : daysDue <= 7 ? 'warning' : 'info'}>
                        {daysDue < 0
                          ? t('ui.desk.daysOverdue', { count: -daysDue })
                          : t('ui.desk.daysLeft', { count: daysDue })}
                      </Badge>
                    </div>
                  </li>
                );
              })}
            </ol>
          </Card>
        </>
      )}

      {reporter && (
        <>
          <SectionTitle>{t('ui.home.sitesDue')}</SectionTitle>
          {work.isLoading && !workMissing ? (
            <Skeleton className="h-20 w-full" />
          ) : (
            <div className="flex flex-col gap-2">
              {dueSites.map((s) => (
                <SiteCard key={s.id} site={s} />
              ))}
              {dueSites.length === 0 && <EmptyState icon="checkCircle" title={t('ui.home.nothingDue')} />}
            </div>
          )}
        </>
      )}

      {can('survey.review') && work.data?.analyst && (
        <>
          <SectionTitle action={<ViewAll to={HOME_LINKS.reviewQueue} />}>{t('ui.home.reviewQueue')}</SectionTitle>
          <div className="flex flex-col gap-2">
            {work.data.analyst.reviewQueue.slice(0, PREVIEW).map((s) => (
              <SurveyRow key={s.id} s={s} to={`/surveys/${s.id}/review`} />
            ))}
            {work.data.analyst.reviewQueue.length === 0 && <EmptyState icon="checkCircle" title={t('ui.home.queueEmpty')} />}
          </div>
        </>
      )}

      {can('finding.close') && work.data?.analyst && (
        <>
          <SectionTitle action={<ViewAll to={HOME_LINKS.findingsToVerify} />}>{t('ui.home.findingsToVerify')}</SectionTitle>
          <div className="flex flex-col gap-2">
            {work.data.analyst.awaitingVerification.slice(0, PREVIEW).map((f) => (
              <FindingRow key={f.id} f={f} />
            ))}
            {work.data.analyst.awaitingVerification.length === 0 && <EmptyState icon="checkCircle" title={t('ui.home.nothingToVerify')} />}
          </div>
        </>
      )}

      {can('survey.communicate') && work.data?.accountManager && (
        <>
          <SectionTitle action={<ViewAll to={HOME_LINKS.readyToSend} />}>{t('ui.home.toCommunicate')}</SectionTitle>
          <div className="flex flex-col gap-2">
            {work.data.accountManager.toCommunicate.slice(0, PREVIEW).map((s) => (
              <SurveyRow key={s.id} s={s} status="CONFIRMED" to={`/surveys/${s.id}`} />
            ))}
            {work.data.accountManager.toCommunicate.length === 0 && <EmptyState icon="checkCircle" title={t('ui.home.queueEmpty')} />}
          </div>

          <SectionTitle action={<ViewAll to={HOME_LINKS.findingsWithoutPlan} />}>{t('ui.home.findingsWithoutPlan')}</SectionTitle>
          <div className="flex flex-col gap-2">
            {work.data.accountManager.findingsWithoutActionPlan.slice(0, PREVIEW).map((f) => (
              <FindingRow key={f.id} f={f} />
            ))}
            {work.data.accountManager.findingsWithoutActionPlan.length === 0 && <EmptyState icon="checkCircle" title={t('ui.home.nothingWithoutPlan')} />}
          </div>
        </>
      )}

      {can('reports.view') && work.data?.manager && (
        <>
          <SectionTitle action={<Link to="/dashboard" className="text-sm font-semibold text-brand">{t('ui.nav.dashboard')}</Link>}>{t('ui.home.overview')}</SectionTitle>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KpiTile label={t('ui.home.kpi.overdueSurveys')} value={work.data.manager.overdueSurveys} />
            <KpiTile
              label={t('ui.home.kpi.openFindings')}
              value={Object.values(work.data.manager.openFindingsBySeverity as Record<Severity, number>).reduce((a, b) => a + b, 0)}
            />
            <KpiTile label={t('ui.home.kpi.reviewsOverdue')} value={work.data.manager.reviewsPastDeadline} />
          </div>
        </>
      )}

      <Dialog
        open={!!activeBulletin}
        onClose={() => setActiveBulletin(null)}
        title={t('ui.safety.bulletin.verifyModalTitle')}
        actions={
          <>
            <Button variant="secondary" onClick={() => setActiveBulletin(null)}>
              {t('ui.common.cancel')}
            </Button>
            <Button variant="primary" loading={verifying} disabled={!verifySiteId} onClick={submitBulletinVerification}>
              {t('ui.safety.bulletin.submitVerify')}
            </Button>
          </>
        }
      >
        {activeBulletin && (
          <div className="flex flex-col gap-3">
            <div className="rounded-lg bg-surface-2 p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold">{activeBulletin.title}</p>
                {activeBulletin.questions.length > 1 && (
                  <Badge tone="brand">{activeBulletin.questions.length}-Question Safety Blitz</Badge>
                )}
              </div>
              <p className="mt-1 text-sm text-muted">{activeBulletin.description}</p>
              {activeBulletin.questions.length <= 1 ? (
                <p className="mt-2 text-sm font-semibold">
                  {t('ui.safety.bulletin.questionLabel')}: {activeBulletin.questions[0] ?? activeBulletin.title}
                </p>
              ) : (
                <div className="mt-2.5 flex flex-col gap-1.5 border-t border-border/60 pt-2.5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted">
                    Rapid Safety Blitz Checklist (uncheck any item requiring action):
                  </p>
                  {activeBulletin.questions.map((q, idx) => {
                    const checked = checkedQuestions[idx] !== false;
                    return (
                      <label
                        key={idx}
                        className="flex cursor-pointer items-start gap-2 rounded-md bg-surface px-2.5 py-1.5 text-xs"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = { ...checkedQuestions, [idx]: e.target.checked };
                            setCheckedQuestions(next);
                            const anyFailed = activeBulletin.questions.some((_item, i) => next[i] === false);
                            setVerifyStatus(anyFailed ? 'ACTION_REQUIRED' : 'SAFE');
                          }}
                          className="mt-0.5 size-4 rounded border-border text-brand"
                        />
                        <span className={checked ? 'text-fg' : 'font-semibold text-danger'}>
                          {idx + 1}. {q}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <Field label={t('ui.safety.table.site')}>
              {(id) => (
                <Select id={id} value={verifySiteId} onChange={(e) => setVerifySiteId(e.target.value)}>
                  {activeBulletin.sites
                    .filter((s) => !s.verified)
                    .map((s) => (
                      <option key={s.siteId} value={s.siteId}>
                        {s.siteName}
                      </option>
                    ))}
                </Select>
              )}
            </Field>
            <Segmented<'SAFE' | 'ACTION_REQUIRED'>
              label={t('ui.safety.bulletin.questionLabel')}
              value={verifyStatus}
              onChange={(v) => v && setVerifyStatus(v)}
              options={[
                { value: 'SAFE', label: t('ui.safety.bulletin.statusSafe'), tone: 'success' },
                { value: 'ACTION_REQUIRED', label: t('ui.safety.bulletin.statusAction'), tone: 'danger' },
              ]}
            />
            <Field label={t('ui.safety.bulletin.notesLabel')}>
              {(id) => (
                <Textarea
                  id={id}
                  rows={2}
                  value={verifyNotes}
                  onChange={(e) => setVerifyNotes(e.target.value)}
                />
              )}
            </Field>
          </div>
        )}
      </Dialog>
    </div>
  );
}
